import { useEffect, useState } from "react";
import { authFetch } from "../../../lib/authFetch";
import { argentinaDateKey } from "../model/towerShared";
import type { TowerDashboard } from "../../../types/controlTower";

const MINUTE = 60_000;
// Tope de espera de una lectura: el backend corta a los 20 s; más allá de esto
// el pedido se da por perdido y la próxima vuelta lo reintenta.
const REQUEST_TIMEOUT_MS = 30_000;

// Lee el tablero de un día. Si tarda más del tope, se cancela con un mensaje
// claro; si lo cancela `signal` (se cerró la vista), se propaga sin mensaje.
export async function fetchTowerDay(date: string, token: string | null, signal: AbortSignal): Promise<TowerDashboard> {
  const controller = new AbortController();
  const cancel = () => controller.abort();
  signal.addEventListener("abort", cancel);
  let timedOut = false;
  const timer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, REQUEST_TIMEOUT_MS);
  try {
    const query = new URLSearchParams({ date, shippingType: "all" });
    const res = await authFetch(`/v1/control-tower/drivers?${query}`, { token, signal: controller.signal });
    const payload = await res.json();
    if (!res.ok || !payload.ok) throw new Error(payload.error || "No se pudieron cargar los choferes.");
    return payload;
  } catch (err) {
    if (timedOut) throw new Error("La Torre tardó demasiado en responder.");
    throw err;
  } finally {
    window.clearTimeout(timer);
    signal.removeEventListener("abort", cancel);
  }
}

export type LightdataSyncStatus = {
  // "unknown": la última consulta falló; no se sabe si sigue sincronizando.
  status: "ok" | "stale" | "never_synced" | "unknown" | string;
  lastSuccessfulSync: string | null;
};

// Corre `load` al montar, cada minuto mientras la pestaña está visible y al
// volver a ella. Una carga en curso no se repite; al desmontar se cancela.
function usePolling(load: (signal: AbortSignal) => Promise<void>, enabled: boolean, deps: unknown[]) {
  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    let inFlight = false;
    const run = async () => {
      if (inFlight || document.hidden) return;
      inFlight = true;
      try {
        await load(controller.signal);
      } finally {
        inFlight = false;
      }
    };
    run();
    const timer = window.setInterval(run, MINUTE);
    document.addEventListener("visibilitychange", run);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", run);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Día argentino y reloj de la Torre: se actualizan cada minuto y al volver a
// la pestaña, así la jornada cambia sola a medianoche y la inactividad avanza.
export function useTowerClock() {
  const [date, setDate] = useState(() => argentinaDateKey());
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    const tick = () => {
      setDate(argentinaDateKey());
      setNowMs(Date.now());
    };
    const timer = window.setInterval(tick, MINUTE);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", tick);
    };
  }, []);
  return { date, nowMs };
}

// Tablero del día de la Torre. `refresh` fuerza otra lectura (botón Reintentar).
// refreshing: hay una lectura en curso; updatedAt: cuándo llegó la última buena.
export function useTowerDashboard(token: string | null, date: string, refresh: number) {
  const [data, setData] = useState<TowerDashboard | null>(null);
  const [error, setError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<number | null>(null);
  usePolling(
    async (signal) => {
      // Pasada la medianoche no se vuelve a pedir el día que terminó: el reloj
      // cambia la fecha en el próximo minuto y se lee el día nuevo.
      if (argentinaDateKey() !== date) return;
      setRefreshing(true);
      try {
        const payload = await fetchTowerDay(date, token, signal);
        if (!signal.aborted) {
          setData(payload);
          setError("");
          setUpdatedAt(Date.now());
        }
      } catch (err) {
        if (!signal.aborted) setError(err instanceof Error ? err.message : "No se pudieron cargar los choferes.");
      } finally {
        if (!signal.aborted) setRefreshing(false);
      }
    },
    Boolean(token && date),
    [token, date, refresh],
  );
  return { data, error, refreshing, updatedAt };
}

// Salud de la sincronización con Lightdata para el encabezado. Si no responde,
// la Torre sigue funcionando con la lectura de PostgreSQL.
export function useLightdataStatus(token: string | null, refresh: number) {
  const [status, setStatus] = useState<LightdataSyncStatus | null>(null);
  usePolling(
    async (signal) => {
      try {
        const res = await authFetch("/v1/lightdata/status", { token, signal });
        const payload = await res.json();
        if (!res.ok || !payload.ok) throw new Error("estado no disponible");
        if (!signal.aborted) setStatus(payload);
      } catch {
        // Sin respuesta, el último "OK" ya no vale: queda desconocido, con la
        // última sincronización exitosa conocida.
        if (!signal.aborted)
          setStatus((previous) => ({ status: "unknown", lastSuccessfulSync: previous?.lastSuccessfulSync ?? null }));
      }
    },
    Boolean(token),
    [token, refresh],
  );
  return status;
}
